import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Spin,
  message,
  Divider,
  Button,
  Typography,
  Dropdown,
  Checkbox,
} from "antd";
import React, { useCallback, useMemo, useState } from "react";
import {
  PrinterOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import XLSX from "xlsx-js-style";
import {
  HEADERS_EXPORT_BEN,
  HEADERS_EXPORT_DETAIL,
  POST_LOAN_DB2_V2,
  POST_REPORT_IMPOUND,
} from "../../API/apiUrls";
import CurrencyFormat from "../../../hook/CurrencyFormat";
import "../../../assets/font/THSarabunNew-normal";
import "../../../assets/font/THSarabunNew-bold";
import MotionHoc from "../../../utils/MotionHoc";
import TokenCheck from "../../../hook/TokenCheck";
import ThaiDatePicker, { ThaiRangePicker } from "../../../hook/ThaiDatePicker";

const TwoLineHeader = ({ top, bottom }) => (
  <div style={{ textAlign: "center", lineHeight: 1.4 }}>
    <div>{top}</div>
    <div style={{ fontSize: 11, fontWeight: 400, color: "#888" }}>{bottom}</div>
  </div>
);
// คำนวณยอดรวม ใช้ร่วมกับ table summary / excel / pdf
const calcTotals = (data) =>
  data.reduce(
    (acc, r) => {
      const total = Number(r.sold_out_price) || 0;
      const beforeVat = total / 1.07;
      const vat = total - beforeVat;
      const cost = Number(r.creditDebtorOld) || 0;
      const profit = total - cost;
      return {
        beforeVat: acc.beforeVat + beforeVat,
        vat: acc.vat + vat,
        total: acc.total + total,
        cost: acc.cost + cost,
        profit: acc.profit + profit,
      };
    },
    { beforeVat: 0, vat: 0, total: 0, cost: 0, profit: 0 },
  );

const Main = () => {
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  const [signOut] = TokenCheck();
  const [loading, setLoading] = useState(false);
  const [arrayPay, setArrayPay] = useState([]);
  const [date, setDate] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);
  const [isFlagAgreement, setIsFlagAgreement] = useState(true);
  const [isFlagEnforce, setIsFlagEnforce] = useState(true);

  // ── Query ─────────────────────────────────────────────
  const onQuery = () => {
    if (!date || date.length < 2 || !date[0] || !date[1]) {
      message.warning("กรุณาเลือกวันที่ก่อนค้นหา");
      return;
    }
    queryData({
      date1: dayjs(date[0]).format("YYYY-MM-DDTHH:mm:ssZ"),
      date2: dayjs(date[1]).format("YYYY-MM-DDTHH:mm:ssZ"),
      currentStatus: 25,
    });
  };

  const queryData = async (payload) => {
    setLoading(true);
    try {
      const res = await axios.post(POST_REPORT_IMPOUND, payload, {
        headers: HEADERS_EXPORT_DETAIL,
      });
      if (res.status !== 200) {
        setArrayPay([]);
        return;
      }

      const reportData = res.data?.data ?? [];

      if (!reportData.length) {
        setArrayPay([]);
        return;
      }

      const groupedBySchema = reportData.reduce((acc, item) => {
        if (!item.CONTNO || !item.SCHEMA) return acc;
        if (!acc[item.SCHEMA]) acc[item.SCHEMA] = [];
        acc[item.SCHEMA].push(`'${item.CONTNO}'`);
        return acc;
      }, {});

      const schemaPayload = Object.entries(groupedBySchema).reduce(
        (acc, [schema, arr]) => {
          acc[schema] = arr.join(",");
          return acc;
        },
        {},
      );

      let db2Map = {};
      try {
        const db2Res = await axios.post(POST_LOAN_DB2_V2, schemaPayload, {
          headers: HEADERS_EXPORT_BEN,
        });
        const db2Data = Array.isArray(db2Res.data)
          ? db2Res.data
          : Array.isArray(db2Res.data?.data)
            ? db2Res.data.data
            : [];

        db2Data.forEach((item) => {
          const contno = item?.LOAN?.CONTNO;
          if (!contno) return;
          db2Map[contno] = {
            LOCAT: item.LOAN?.LOCAT,
            TOTPRC: item.LOAN?.TOTPRC,
            NCSHPRC: item.LOAN?.NCSHPRC,
            SMPAY: item.LOAN?.SMPAY,
            PRODUCTLOAN: item.LOAN?.PRODUCTLOAN,
            ISSUNO: item.LOAN?.ISSUNO,
            TYPE: item.MORTGAGE?.TYPE,
            MODEL: item.MORTGAGE?.MODEL,
            BAAB: item.MORTGAGE?.BAAB,
            COLOR: item.MORTGAGE?.COLOR,
            REGNO: item.MORTGAGE?.REGNO,
            ENGNO: item.MORTGAGE?.ENGNO,
            MANUYR: item.MORTGAGE?.MANUYR,
            DORECV: item.MORTGAGE?.DORECV,
            OFFIC: item.CUSTOMER?.OFFIC,
            OCCUP: item.CUSTOMER?.OCCUP,
            BIRTHDT: item.CUSTOMER?.BIRTHDT,
            ADDRESS: item.CUSTOMER?.ADDRESS ?? [],
            GUARANTORS: item.GUARANTORS ?? [],
          };
        });
      } catch (e) {
        console.warn("DB2 failed:", e);
      }
      setArrayPay(
        reportData
          .map((item) => ({ ...item, ...(db2Map[item.CONTNO] ?? {}) }))
          .sort((a, b) => dayjs(a.SDATE).valueOf() - dayjs(b.SDATE).valueOf()),
      );
    } catch (err) {
      message.error(
        err?.response?.status === 404 ? "ไม่พบข้อมูล" : "เกิดข้อผิดพลาด",
      );
      if (err?.response?.status === 401) {
        signOut();
      }
      setArrayPay([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterData = useCallback(() => {
    if (!arrayPay.length) return [];

    return arrayPay.filter((item) => {
      // ✅ ตัด closeContno = 1 ออกเสมอ
      if (item.closeContno === 1) return false;

      // ✅ ทั้งคู่ true → เอาทั้งหมด
      if (isFlagAgreement && isFlagEnforce) return true;

      // ✅ Agreement อย่างเดียว → flagLoanNew = 1
      if (isFlagAgreement && !isFlagEnforce) return item.flagLoanNew === 1;

      // ✅ Enforce อย่างเดียว → flagLoanNew = 2
      if (!isFlagAgreement && isFlagEnforce) return item.flagLoanNew === 2;

      // ✅ ทั้งคู่ false → ไม่มีข้อมูล
      return false;
    });
  }, [arrayPay, isFlagAgreement, isFlagEnforce]);

  // ── Columns ───────────────────────────────────────────
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 60,
      render: (_, __, i) => i + 1,
    },
    {
      title: "สาขา",
      align: "center",
      width: 60,
      render: (_, r) => (
        <>
          <div style={{ fontWeight: 600 }}>{r.LOCAT || "-"}</div>
        </>
      ),
    },
    {
      title: "เลขตัวถัง",
      align: "center",
      width: 60,
      render: (_, r) => (
        <>
          <div style={{ fontWeight: 600 }}>{r.STRNO || "-"}</div>
        </>
      ),
    },
    {
      title: <TwoLineHeader top="เลขที่ใบเสร็จ" bottom="วันที่ออกใบเสร็จ" />,
      key: "doc_no",
      align: "center",
      width: 130,
      render: (_, r) => (
        <div>
          {r.doc_no || "-"}
          <br />
          {dayjs(r.doc_dt).format("DD/MM/YYYY")}
        </div>
      ),
    },
    {
      title: "เลขสัญญา",
      key: "contract_model",
      align: "center",
      width: 130,
      render: (_, r) => (
        <>
          <div style={{ fontWeight: 600 }}>{r.newContno || "-"}</div>
        </>
      ),
    },
    {
      title: <TwoLineHeader top="วันทำสัญญา" bottom="ชื่อลูกค้า" />,
      key: "name_strno",
      align: "left",
      width: 140,
      render: (_, r) => (
        <div>
          {dayjs(r.SDATE).format("DD/MM/YYYY")}
          <br />
          {`${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim() || "-"}
        </div>
      ),
    },

    {
      title: "วันที่ขาย",
      align: "center",
      width: 90,
      sorter: (a, b) =>
        dayjs(a.sold_out_date).valueOf() - dayjs(b.sold_out_date).valueOf(),
      render: (_, r) => (
        <>
          <div>
            {r.sold_out_date
              ? dayjs(r.sold_out_date).add(543, "year").format("DD/MM/YYYY")
              : "-"}
          </div>
        </>
      ),
    },
    {
      title: "ราคาขาย",
      key: "totalJugde",
      align: "center",
      width: 70,
      render: (val) => (
        <div>
          {currencyFormatPoint((Number(val?.sold_out_price) || 0) / 1.07)}
        </div>
      ),
    },
    {
      title: "ราคาขาย (รวม)",
      align: "right",
      width: 100,
      render: (_, r) => <div>{currencyFormatPoint(r.sold_out_price)}</div>,
    },
    {
      title: "ต้นทุนเดิม",
      align: "right",
      width: 100,
      render: (_, r) => <div>{currencyFormatPoint(r.creditDebtorOld)}</div>,
    },
    {
      title: "กำไร",
      align: "right",
      width: 100,
      render: (_, r) => (
        <div>{currencyFormatPoint(r.sold_out_price - r.creditDebtorOld)}</div>
      ),
    },
  ];

  // ── Export Excel ──────────────────────────────────────
  const handleExportExcel = () => {
    if (!filteredData.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const fromDate = date[0] ? dayjs(date[0]).format("DD/MM/YYYY") : "";
    const toDate = date[1] ? dayjs(date[1]).format("DD/MM/YYYY") : "";

    const rows = filteredData.map((r, i) => {
      const total = Number(r.sold_out_price) || 0;
      const beforeVat = total / 1.07;
      const vat = total - beforeVat;
      const cost = Number(r.creditDebtorOld) || 0;
      const profit = total - cost;
      return {
        ลำดับ: i + 1,
        สาขา: r.LOCAT || "",
        เลขตัวถัง: r.STRNO || "",
        เลขที่ใบเสร็จ: r.doc_no || "",
        วันที่ออกใบเสร็จ: r.doc_dt ? dayjs(r.doc_dt).format("DD/MM/YYYY") : "",
        เลขสัญญา: r.newContno || "",
        วันทำสัญญา: r.SDATE ? dayjs(r.SDATE).format("DD/MM/YYYY") : "",
        ชื่อลูกค้า: `${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim(),
        วันที่ขาย: r.sold_out_date
          ? dayjs(r.sold_out_date).add(543, "year").format("DD/MM/YYYY")
          : "",
        ราคาขาย: beforeVat,
        "vat (7%)": vat,
        "ราคาขาย (รวม)": total,
        ต้นทุนเดิม: cost,
        กำไร: profit,
      };
    });

    const moneyKeys = [
      "ราคาขาย",
      "vat (7%)",
      "ราคาขาย (รวม)",
      "ต้นทุนเดิม",
      "กำไร",
    ];
    const t = calcTotals(filteredData);

    const summaryRow = {
      ลำดับ: "",
      สาขา: `รวมทั้งหมด ${filteredData.length} รายการ`,
      เลขตัวถัง: "",
      เลขที่ใบเสร็จ: "",
      วันที่ออกใบเสร็จ: "",
      เลขสัญญา: "",
      วันทำสัญญา: "",
      ชื่อลูกค้า: "",
      วันที่ขาย: "",
      ราคาขาย: t.beforeVat,
      "vat (7%)": t.vat,
      "ราคาขาย (รวม)": t.total,
      ต้นทุนเดิม: t.cost,
      กำไร: t.profit,
    };

    const allRows = [...rows, summaryRow];
    const ws = XLSX.utils.json_to_sheet(allRows);
    const keys = Object.keys(rows[0]);

    const borderStyle = {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    };

    const getAlignment = (key) => {
      if (key === "ชื่อลูกค้า") return "left";
      if (moneyKeys.includes(key)) return "right";
      return "center";
    };

    // header
    keys.forEach((_, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!ws[cellRef]) return;
      ws[cellRef].s = {
        fill: { patternType: "solid", fgColor: { rgb: "344962" } },
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
        alignment: { horizontal: "center", vertical: "center" },
        border: borderStyle,
      };
    });

    // data rows
    rows.forEach((_, ri) => {
      const fill = {
        patternType: "solid",
        fgColor: { rgb: ri % 2 === 0 ? "EBF5FF" : "FFFFFF" },
      };
      keys.forEach((key, ci) => {
        const cellRef = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (!ws[cellRef]) ws[cellRef] = { v: "", t: "s" };
        const isMoney = moneyKeys.includes(key);
        ws[cellRef].s = {
          fill,
          font: { sz: 11 },
          alignment: { horizontal: getAlignment(key), vertical: "center" },
          border: borderStyle,
          numFmt: isMoney ? "#,##0.00" : undefined,
        };
      });
    });

    // summary row
    const summaryRowIdx = rows.length + 1;
    keys.forEach((key, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: summaryRowIdx, c: ci });
      if (!ws[cellRef]) return;
      const isMoney = moneyKeys.includes(key);
      ws[cellRef].s = {
        fill: { patternType: "solid", fgColor: { rgb: "D5E8D4" } },
        font: { bold: true, sz: 12 },
        alignment: { horizontal: getAlignment(key), vertical: "center" },
        border: borderStyle,
        numFmt: isMoney ? "#,##0.00" : undefined,
      };
    });

    ws["!cols"] = keys.map((key) => ({ wch: Math.max(key.length * 2.5, 16) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "รายงาน");
    XLSX.writeFile(wb, `รายงานขายทอดตลาด_${fromDate}_${toDate}.xlsx`);
    message.success("Export Excel สำเร็จ");
  };

  // ── Export PDF ────────────────────────────────────────
  const handlePrintPDF = () => {
    if (!filteredData.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const now = dayjs().format("DD/MM/YYYY HH:mm:ss");
    const fromDate = date[0] ? dayjs(date[0]).format("DD/MM/YYYY") : "";
    const toDate = date[1] ? dayjs(date[1]).format("DD/MM/YYYY") : "";

    const drawHeader = (pageNum) => {
      doc.setFont("THSarabunNew", "bold");
      doc.setFontSize(16);
      doc.text("รายงานขายทอดตลาด", pageWidth / 2, 8, { align: "center" });
      doc.text(
        `ขายจากวันที่  ${fromDate}   ถึงวันที่  ${toDate}`,
        pageWidth / 2,
        14,
        { align: "center" },
      );
      doc.setFont("THSarabunNew", "normal");
      doc.setFontSize(14);
      doc.text("บริษัท วัน เอเอ็มซี จำกัด (สำนักงานใหญ่)", 8, 20);
      doc.text(`วันที่พิมพ์ ${now}`, 8, 26);
      doc.text(`หน้าที่ ${pageNum}`, pageWidth - 8, 26, { align: "right" });
    };

    const tableBody = filteredData.map((r, i) => {
      const total = Number(r.sold_out_price) || 0;
      const beforeVat = total / 1.07;
      const cost = Number(r.creditDebtorOld) || 0;
      const profit = total - cost;
      const customerName =
        `${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim() || "-";
      return [
        i + 1,
        r.LOCAT || "-",
        r.STRNO || "-",
        `${r.doc_no || "-"}\n${r.doc_dt ? dayjs(r.doc_dt).format("DD/MM/YYYY") : ""}`,
        r.newContno || "-",
        `${r.SDATE ? dayjs(r.SDATE).format("DD/MM/YYYY") : ""}\n${customerName}`,
        r.sold_out_date
          ? dayjs(r.sold_out_date).add(543, "year").format("DD/MM/YYYY")
          : "-",
        currencyFormatPoint(beforeVat),
        currencyFormatPoint(total),
        currencyFormatPoint(cost),
        currencyFormatPoint(profit),
      ];
    });

    const t = calcTotals(filteredData);
    const summaryStyle = {
      fontStyle: "bold",
      font: "THSarabunNew",
      fillColor: [236, 240, 241],
    };

    const summaryRow = [
      {
        content: `รวมทั้งหมด ${filteredData.length} รายการ`,
        colSpan: 7,
        styles: { ...summaryStyle, halign: "center" },
      },
      {
        content: currencyFormatPoint(t.beforeVat),
        styles: { ...summaryStyle, halign: "right" },
      },
      {
        content: currencyFormatPoint(t.total),
        styles: { ...summaryStyle, halign: "right" },
      },
      {
        content: currencyFormatPoint(t.cost),
        styles: { ...summaryStyle, halign: "right" },
      },
      {
        content: currencyFormatPoint(t.profit),
        styles: { ...summaryStyle, halign: "right" },
      },
    ];

    doc.autoTable({
      startY: 30,
      head: [
        [
          "ลำดับ",
          "สาขา",
          "เลขตัวถัง",
          "เลขที่ใบเสร็จ\nวันที่ออกใบเสร็จ",
          "เลขสัญญา",
          "วันทำสัญญา\nชื่อลูกค้า",
          "วันที่ขาย",
          "ราคาขาย",
          "ราคาขาย (รวม)",
          "ต้นทุนเดิม",
          "กำไร",
        ],
      ],
      body: [...tableBody, summaryRow],
      headStyles: {
        font: "THSarabunNew",
        fillColor: [52, 73, 94],
        textColor: 255,
        halign: "center",
        fontStyle: "bold",
        fontSize: 13,
      },
      columnStyles: {
        5: { halign: "left" }, // วันทำสัญญา/ชื่อลูกค้า
        7: { halign: "right" }, // ราคาขาย
        8: { halign: "right" }, // ราคาขาย (รวม)
        9: { halign: "right" }, // ต้นทุนเดิม
        10: { halign: "right" }, // กำไร
      },
      tableWidth: "auto",
      margin: { top: 30, left: 3, right: 3 },
      alternateRowStyles: { fillColor: [235, 245, 255] },
      styles: {
        font: "THSarabunNew",
        fontSize: 12,
        cellPadding: 1,
        halign: "center",
        fillColor: [255, 255, 255],
      },
      didDrawPage: () =>
        drawHeader(doc.internal.getCurrentPageInfo().pageNumber),
    });

    doc.save(`รายงานขายทอดตลาด_${fromDate}_${toDate}.pdf`);
  };

  // ✅ Dropdown menu เลือก PDF หรือ Excel
  const exportMenuItems = {
    items: [
      {
        key: "pdf",
        label: "Export PDF",
        icon: <FilePdfOutlined style={{ color: "#c0392b" }} />,
      },
      {
        key: "excel",
        label: "Export Excel",
        icon: <FileExcelOutlined style={{ color: "#27ae60" }} />,
      },
    ],
    onClick: ({ key }) => {
      if (key === "pdf") handlePrintPDF();
      if (key === "excel") handleExportExcel();
    },
  };

  const hasDate = date[0] && date[1];
  const filteredData = useMemo(() => handleFilterData(), [handleFilterData]);
  console.log("filteredData", filteredData);
  console.log("filteredData", filteredData);

  return (
    <>
      <style>{`
      .row-even td { background-color: #f5f8ff !important; }
      .row-odd td  { background-color: #ffffff !important; }
    `}</style>
      <Spin spinning={loading} size="large" tip="Loading...">
        <Row>
          <Col span={16}>
            <Space style={{ marginBottom: 10 }}>
              เริ่ม
              <ThaiDatePicker
                size="large"
                value={date[0]}
                onChange={(value) => setDate([value, date[1]])}
                placeholder={"วันที่เริ่มต้น"}
                allowClear={false}
              />
              ถึง
              <ThaiDatePicker
                size="large"
                value={date[1]}
                onChange={(value) => setDate([date[0], value])}
                placeholder={"วันที่สิ้นสุด"}
                allowClear={false}
              />
              <Button size="large" type="primary" onClick={onQuery}>
                ค้นหา
              </Button>
              <Checkbox
                checked={isFlagAgreement}
                onChange={(e) => setIsFlagAgreement(e.target.checked)}
              >
                ทำยอม
              </Checkbox>
              <Checkbox
                checked={isFlagEnforce}
                onChange={(e) => setIsFlagEnforce(e.target.checked)}
              >
                พิพากษา
              </Checkbox>
            </Space>
          </Col>
          <Col span={8} style={{ textAlign: "end" }}>
            <Space style={{ marginBottom: 10 }}>
              {/* ✅ Dropdown เลือก PDF / Excel */}
              <Dropdown menu={exportMenuItems} disabled={!filteredData.length}>
                <Button size="large" icon={<PrinterOutlined />}>
                  พิมพ์ / Export <DownOutlined />
                </Button>
              </Dropdown>
            </Space>
          </Col>
        </Row>

        <Card style={{ marginBottom: 10 }}>
          <Divider style={{ fontSize: 20 }}>รายงานขายทอดตลาด</Divider>

          <Typography.Text
            strong
            style={{ display: "block", textAlign: "center", fontSize: 16 }}
          >
            บริษัท วัน เอเอ็มซี จำกัด (สำนักงานใหญ่)
          </Typography.Text>

          {hasDate && (
            <Typography.Text
              style={{ display: "block", textAlign: "center", fontSize: 16 }}
            >
              ขายจากวันที่ {dayjs(date[0]).format("DD/MM/YYYY")} ถึงวันที่{" "}
              {dayjs(date[1]).format("DD/MM/YYYY")}
            </Typography.Text>
          )}

          <Table
            style={{ marginTop: 10 }}
            size="small"
            columns={columns}
            dataSource={filteredData}
            rowKey={(r) => r.idJudge ?? r.CONTNO}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "row-even" : "row-odd"
            }
            scroll={{ x: 1400 }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              showTotal: (t) => `ทั้งหมด ${t} รายการ`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            summary={() => {
              const t = calcTotals(filteredData);
              const cs = { fontWeight: "bold", background: "#ecf0f1" };
              const cr = {
                fontWeight: "bold",
                background: "#ecf0f1",
                textAlign: "right",
              };

              return (
                <Table.Summary.Row>
                  {/* ลำดับ, สาขา, เลขตัวถัง, ใบเสร็จ, เลขสัญญา, วันทำสัญญา/ชื่อ, วันที่ขาย → 7 ช่อง */}
                  <Table.Summary.Cell colSpan={7} align="center" style={cs}>
                    รวมทั้งหมด {filteredData.length} รายการ
                  </Table.Summary.Cell>

                  {/* ราคาขาย (ก่อน VAT) */}
                  <Table.Summary.Cell align="right" style={cr}>
                    {currencyFormatPoint(t.beforeVat)}
                  </Table.Summary.Cell>

                  {/* ราคาขาย (รวม) */}
                  <Table.Summary.Cell align="right" style={cr}>
                    {currencyFormatPoint(t.total)}
                  </Table.Summary.Cell>

                  {/* ต้นทุนเดิม */}
                  <Table.Summary.Cell align="right" style={cr}>
                    {currencyFormatPoint(t.cost)}
                  </Table.Summary.Cell>

                  {/* กำไร */}
                  <Table.Summary.Cell align="right" style={cr}>
                    {currencyFormatPoint(t.profit)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </Card>
      </Spin>
    </>
  );
};

const MainSellingCar = MotionHoc(Main);
export default MainSellingCar;
